import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Message, Input } from 'semantic-ui-react';
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
  change,
} from 'redux-form';
import { formatCurrencyRupiah } from 'utils/currencyUtils';
import {
  showCreatePackageModal,
  showEditPackageModal,
  savePackage,
  getTerritories,
  getLeadSources,
  getBonusSubscriptionTypes,
} from 'state/billingPackage/actions';
import {
  createSubscriptionProductOptions,
  mapPackageDataToPayload,
  mapPackageToFormValues,
} from 'state/billingPackage/mappers';
import {
  requiredFieldValidation,
  minValueValidation,
  rangeValidation,
  requiredArrayValidation,
} from 'utils/formUtils';
import { renderCheckboxGrid } from './components/FormFieldRenderers';

import BasicInfoSection from './components/BasicInfoSection';
import EligiblePicSection from './components/EligiblePicSection';
import ActivePeriodSection from './components/ActivePeriodSection';
import ExtraSubscriptionSection from './components/ExtraSubscriptionSection';
import ApprovalConfigSection from './components/ApprovalConfigSection';

const DiscountPercentageInput = ({ input }) => (
  <Input {...input} type="number" min={0} max={100} fluid />
);

DiscountPercentageInput.propTypes = {
  input: PropTypes.object.isRequired,
};

const validate = values => {
  const errors = {};

  const nameError = requiredFieldValidation(values.name, 'Package name');
  if (nameError) {
    errors.name = nameError;
  }

  const subscriptionProductError = requiredFieldValidation(
    values.subscription_product_id,
    'Base module'
  );
  if (subscriptionProductError) {
    errors.subscription_product_id = subscriptionProductError;
  }

  const quantityRequiredError = requiredFieldValidation(
    values.quantity,
    'Duration'
  );
  if (quantityRequiredError) {
    errors.quantity = quantityRequiredError;
  } else {
    const quantityMinError = minValueValidation(
      Number(values.quantity),
      1,
      'Duration'
    );
    if (quantityMinError) {
      errors.quantity = quantityMinError;
    }
  }

  const platformsError = requiredArrayValidation(
    values.available_platforms,
    'platform type'
  );
  if (platformsError) {
    errors.available_platforms = platformsError;
  }

  const leadSourcesError = requiredArrayValidation(
    values.available_lead_sources,
    'lead source type'
  );
  if (leadSourcesError) {
    errors.available_lead_sources = leadSourcesError;
  }

  if (values.available_for_sales && values.sales_access === 'specific_region') {
    const territoryError = requiredArrayValidation(
      values.available_sales_territory_ids,
      'region'
    );
    if (territoryError) {
      errors.available_sales_territory_ids = territoryError;
    }
  }

  if (
    values.available_for_sales &&
    values.sales_access === 'specific_sales_pic'
  ) {
    const salesEmailError = requiredFieldValidation(
      values.available_sales_emails,
      'Sales PIC'
    );
    if (salesEmailError) {
      errors.available_sales_emails = salesEmailError;
    }
  }

  if (values.bonus_subscription_type === 'CUSTOM') {
    const bonusMonthsRequiredError = requiredFieldValidation(
      values.bonus_subscription_months,
      'Maximum extra months'
    );
    if (bonusMonthsRequiredError) {
      errors.bonus_subscription_months = bonusMonthsRequiredError;
    } else {
      const bonusMonthsRangeError = rangeValidation(
        Number(values.bonus_subscription_months),
        1,
        3,
        'Maximum extra months'
      );
      if (bonusMonthsRangeError) {
        errors.bonus_subscription_months = bonusMonthsRangeError;
      }
    }
  }

  if (values.bonus_subscription_type === 'FIXED') {
    const bonusMonthsRequiredError = requiredFieldValidation(
      values.bonus_subscription_months,
      'Extra months to add'
    );
    if (bonusMonthsRequiredError) {
      errors.bonus_subscription_months = bonusMonthsRequiredError;
    } else {
      const bonusMonthsMinError = minValueValidation(
        Number(values.bonus_subscription_months),
        1,
        'Extra months to add'
      );
      if (bonusMonthsMinError) {
        errors.bonus_subscription_months = bonusMonthsMinError;
      }
    }
  }

  return errors;
};

export class CustomPackageEditorModal extends React.Component {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClose = this.onClose.bind(this);
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(getTerritories());
    dispatch(getLeadSources());
    dispatch(getBonusSubscriptionTypes());
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      discountPercentageValue,
      bonusSubscriptionTypeValue,
      approvalRequirementsValue,
    } = this.props;
    const currentApproval = Array.isArray(approvalRequirementsValue)
      ? approvalRequirementsValue
      : [];

    const discountBecameFalsy =
      prevProps.discountPercentageValue &&
      (!discountPercentageValue || Number(discountPercentageValue) === 0);
    if (discountBecameFalsy && currentApproval.includes('HIGHER_DISCOUNT')) {
      dispatch(
        change(
          'customPackageEditor',
          'approval_requirements',
          currentApproval.filter(t => t !== 'HIGHER_DISCOUNT')
        )
      );
    }

    const bonusBecameNone =
      prevProps.bonusSubscriptionTypeValue !== 'NONE' &&
      bonusSubscriptionTypeValue === 'NONE';
    if (bonusBecameNone && currentApproval.includes('BONUS_SUBSCRIPTION')) {
      dispatch(
        change(
          'customPackageEditor',
          'approval_requirements',
          currentApproval.filter(t => t !== 'BONUS_SUBSCRIPTION')
        )
      );
    }
  }

  onClose() {
    const { dispatch, reset } = this.props;
    dispatch(showCreatePackageModal(false));
    dispatch(showEditPackageModal(false, null, false));
    reset();
  }

  onSubmit(values) {
    const { dispatch, isEditMode, editingPackage } = this.props;
    const isUpgradeEditMode =
      isEditMode && editingPackage && editingPackage.is_upgrade_product;

    if (!isUpgradeEditMode) {
      const errors = validate(values);
      if (Object.keys(errors).length > 0) {
        throw new SubmissionError(errors);
      }
    }

    const packageData = {
      ...values,
      id: isEditMode ? editingPackage.id : null,
    };

    const packagePayload = mapPackageDataToPayload(packageData, isEditMode);
    return dispatch(savePackage(packagePayload, isEditMode));
  }

  render() {
    const {
      isOpen,
      isEditMode,
      isLoading,
      errorMessage,
      handleSubmit,
      basePackages,
      territories,
      leadSources,
      bonusSubscriptionTypes,
      salesAccessValue,
      activePeriodValue,
      bonusSubscriptionTypeValue,
      subscriptionProductValue,
      quantityValue,
      discountPercentageValue,
      availableForSalesValue,
      editingPackage,
    } = this.props;

    const isUpgradeEditMode =
      isEditMode && editingPackage && editingPackage.is_upgrade_product;

    if (isUpgradeEditMode) {
      return (
        <Modal open={isOpen} onClose={this.onClose} size="small">
          <Modal.Header>Edit Upgrade Package Discount</Modal.Header>
          <Modal.Content>
            {errorMessage && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{errorMessage}</p>
              </Message>
            )}
            <Form loading={isLoading}>
              <Form.Field>
                <label>Upgrade Path</label>
                <p style={{ color: '#999' }}>{editingPackage.name}</p>
              </Form.Field>

              <Form.Field>
                <label>Price Delta</label>
                <p style={{ color: '#999' }}>
                  {formatCurrencyRupiah(editingPackage.total_price)}
                </p>
              </Form.Field>

              <Form.Field>
                <label>Discount Threshold (%)</label>
                <Field
                  name="discount_percentage"
                  component={DiscountPercentageInput}
                />
                <p
                  style={{
                    color: '#999',
                    marginTop: '0.5em',
                    fontSize: '0.9em',
                  }}
                >
                  Sales/telesales will need approval if they apply more than
                  this discount on quotation
                </p>
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.onClose}>Cancel</Button>
            <Button
              primary
              onClick={handleSubmit(this.onSubmit)}
              loading={isLoading}
            >
              Save
            </Button>
          </Modal.Actions>
        </Modal>
      );
    }

    const subscriptionProductOptions = createSubscriptionProductOptions(
      basePackages
    );
    const modalTitle = isEditMode
      ? 'Edit Customize Package'
      : 'Create Customize Package';

    return (
      <Modal open={isOpen} onClose={this.onClose} size="large">
        <Modal.Header>{modalTitle}</Modal.Header>
        <Modal.Content scrolling>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)} loading={isLoading}>
            <BasicInfoSection
              subscriptionProductOptions={subscriptionProductOptions}
              subscriptionProductValue={subscriptionProductValue}
              quantityValue={quantityValue}
              discountPercentageValue={discountPercentageValue}
              basePackages={basePackages}
            />

            <Field
              name="available_platforms"
              component={renderCheckboxGrid}
              label="Platform Type"
              options={[
                {
                  value: 'MOKA_BACKOFFICE',
                  label: 'Moka Backoffice',
                },
                {
                  value: 'INTERNAL_TOOL',
                  label: 'Odoo Internal Tools',
                },
              ]}
            />

            <Field
              name="available_lead_sources"
              component={renderCheckboxGrid}
              label="Lead Source Type"
              options={leadSources}
            />

            <EligiblePicSection
              availableForSalesValue={availableForSalesValue}
              salesAccessValue={salesAccessValue}
              territories={territories}
            />

            <ActivePeriodSection activePeriodValue={activePeriodValue} />

            <ExtraSubscriptionSection
              bonusSubscriptionTypeValue={bonusSubscriptionTypeValue}
              bonusSubscriptionTypes={bonusSubscriptionTypes}
            />

            <ApprovalConfigSection
              discountPercentageValue={discountPercentageValue}
              bonusSubscriptionTypeValue={bonusSubscriptionTypeValue}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose}>Cancel</Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            loading={isLoading}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

CustomPackageEditorModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isEditMode: PropTypes.bool,
  editingPackage: PropTypes.object,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  basePackages: PropTypes.array,
  territories: PropTypes.array,
  leadSources: PropTypes.array,
  bonusSubscriptionTypes: PropTypes.array,
  salesAccessValue: PropTypes.string,
  activePeriodValue: PropTypes.string,
  bonusSubscriptionTypeValue: PropTypes.string,
  subscriptionProductValue: PropTypes.number,
  quantityValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discountPercentageValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  availableForSalesValue: PropTypes.bool,
  approvalRequirementsValue: PropTypes.arrayOf(PropTypes.string),
};

CustomPackageEditorModal.defaultProps = {
  isOpen: false,
  isEditMode: false,
  editingPackage: null,
  isLoading: false,
  errorMessage: null,
  basePackages: [],
  territories: [],
  leadSources: [],
  bonusSubscriptionTypes: [],
  salesAccessValue: 'all_regions',
  activePeriodValue: 'live_time',
  bonusSubscriptionTypeValue: 'no',
  subscriptionProductValue: null,
  quantityValue: 1,
  discountPercentageValue: 0,
  availableForSalesValue: true,
  approvalRequirementsValue: [],
};

const selector = formValueSelector('customPackageEditor');

const mapStateToProps = state => {
  const {
    editor,
    basePackages,
    territories,
    leadSources,
    bonusSubscriptionTypes,
  } = state.billingPackage;

  return {
    isOpen: editor.isOpen,
    isEditMode: editor.isEditMode,
    editingPackage: editor.editingPackage,
    isLoading: editor.isLoading,
    errorMessage: editor.errorMessage,
    initialValues: mapPackageToFormValues(
      editor.editingPackage || {
        duration: 1,
        discount: 0,
        available_platforms: [],
        available_lead_sources: [],
        sales_access: 'all_regions',
        available_for_sales: true,
        available_for_all_telesales: true,
        active_period: 'live_time',
        bonus_subscription_type: 'NONE',
        available_sales_territory_ids: [],
        approval_requirements: [],
      }
    ),
    basePackages,
    territories,
    leadSources,
    bonusSubscriptionTypes,
    salesAccessValue: selector(state, 'sales_access'),
    activePeriodValue: selector(state, 'active_period'),
    bonusSubscriptionTypeValue: selector(state, 'bonus_subscription_type'),
    subscriptionProductValue: selector(state, 'subscription_product_id'),
    quantityValue: selector(state, 'quantity'),
    discountPercentageValue: selector(state, 'discount_percentage'),
    availableForSalesValue: selector(state, 'available_for_sales'),
    approvalRequirementsValue: selector(state, 'approval_requirements'),
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'customPackageEditor',
    enableReinitialize: true,
  })(CustomPackageEditorModal)
);
